import { h } from 'preact';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { Button, Spinner } from '../components/ui';
import { T } from '../styles/tokens';

const TYPE_COLORS: Record<string, string> = {
  audit: T.primary,
  vault: T.purple,
  router_errors: T.danger,
  activity: T.success,
  notifications: T.warning,
};

const TYPE_LABELS: Record<string, string> = {
  audit: 'Audit',
  vault: 'Vault',
  router_errors: 'Router Errors',
  activity: 'Atividade',
  notifications: 'Notificações',
};

const SEVERITY_COLORS: Record<string, { bg: string; color: string; fontWeight?: number }> = {
  info: { bg: 'rgba(156,163,175,0.15)', color: T.textSecondary },
  warning: { bg: T.warningBg, color: T.warning },
  error: { bg: T.dangerBg, color: T.danger },
  critical: { bg: T.dangerBg, color: T.danger, fontWeight: 700 },
};

interface LogEntry {
  id: string;
  type: string;
  timestamp: string;
  summary: string;
  details: any;
  severity: string;
  customer_id?: string;
  ip?: string;
}

interface LogsResponse {
  logs: LogEntry[];
  total: number;
  types: Record<string, number>;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const d = new Date(dateStr).getTime();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return `${diff}s atrás`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
  return `${Math.floor(diff / 86400)}d atrás`;
}

export function AdminLogs() {
  // api.get adds /api/v1 prefix automatically
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState<Record<string, number>>({});
  const [activeType, setActiveType] = useState<string>('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const limit = 50;

  const fetchLogs = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeType) params.set('type', activeType);
      if (search) params.set('search', search);
      const currentOffset = reset ? 0 : offset;
      params.set('limit', String(limit));
      params.set('offset', String(currentOffset));

      const json = await api.get(`/admin/logs?${params}`) as any;
      if (json) {
        const data: LogsResponse = json;
        if (reset) {
          setLogs(data.logs);
          setOffset(0);
        } else if (currentOffset === 0) {
          setLogs(data.logs);
        } else {
          setLogs(prev => [...prev, ...data.logs]);
        }
        setTotal(data.total);
        setTypes(data.types);
      }
    } catch (e) {
      console.error('Failed to fetch logs', e);
    }
    setLoading(false);
  }, [activeType, search, offset]);

  useEffect(() => { setOffset(0); fetchLogs(true); }, [activeType, search]);

  const loadMore = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
  };

  useEffect(() => { if (offset > 0) fetchLogs(); }, [offset]);

  const doSearch = () => setSearch(searchInput);

  const allTypes = ['', 'audit', 'vault', 'router_errors', 'activity', 'notifications'];

  const totalAll = Object.values(types).reduce((a, b) => a + b, 0);

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1200, margin: '0 auto', fontFamily: "'Manrope', sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.text }}>Logs do Sistema</h1>
          <span style={{ fontSize: 13, color: T.textMuted }}>{totalAll.toLocaleString()} logs totais</span>
        </div>
        <Button variant="primary" size="sm" onClick={() => fetchLogs(true)} disabled={loading} loading={loading}>
          Atualizar
        </Button>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
        {allTypes.map(t => (
          <Button variant="ghost"
            key={t}
            onClick={() => setActiveType(t)}
            style={{
              background: activeType === t ? (TYPE_COLORS[t] || T.primary) : T.bgInput,
              color: activeType === t ? '#fff' : T.textSecondary,
              border: `1px solid ${activeType === t ? 'transparent' : T.bgSecondary}`,
              borderRadius: 20, padding: '6px 16px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            {t ? TYPE_LABELS[t] : 'Todos'}
          </Button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            placeholder="Buscar nos logs..."
            value={searchInput}
            onInput={(e: any) => setSearchInput(e.target.value)}
            onKeyDown={(e: any) => e.key === 'Enter' && doSearch()}
            style={{
              background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 8,
              padding: '7px 14px', color: T.text, fontSize: 13, width: 220,
              outline: 'none',
            }}
          />
          <Button variant="ghost" size="sm" onClick={doSearch}>Buscar</Button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        {(['audit', 'vault', 'router_errors', 'activity', 'notifications'] as const).map(t => (
          <div key={t} style={{
            background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 10,
            padding: '14px 16px', cursor: 'pointer',
          }} onClick={() => setActiveType(t)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: TYPE_COLORS[t] }} />
              <span style={{ fontSize: 11, color: T.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {TYPE_LABELS[t]}
              </span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>
              {(types[t] || 0).toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {/* Log list */}
      <div style={{ background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {logs.length === 0 && !loading && (
          <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 14 }}>Nenhum log encontrado</div>
        )}
        {logs.map((log, i) => (
          <div key={`${log.type}-${log.id}`}>
            <div
              onClick={() => setExpandedId(expandedId === `${log.type}-${log.id}` ? null : `${log.type}-${log.id}`)}
              style={{
                padding: '12px 18px', cursor: 'pointer',
                background: i % 2 === 0 ? T.bgInput : '#0f1520',
                borderBottom: `1px solid ${T.border}`,
                display: 'flex', alignItems: 'center', gap: 12,
                transition: 'background 0.1s',
              }}
            >
              {/* Timestamp */}
              <span title={new Date(log.timestamp).toLocaleString('pt-BR')} style={{ fontSize: 11, color: T.textMuted, minWidth: 70, fontFamily: 'monospace' }}>
                {timeAgo(log.timestamp)}
              </span>
              {/* Type badge */}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                background: `${TYPE_COLORS[log.type]}22`, color: TYPE_COLORS[log.type],
                textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 70, textAlign: 'center',
              }}>
                {TYPE_LABELS[log.type] || log.type}
              </span>
              {/* Severity badge */}
              {log.severity && log.severity !== 'info' && (
                <span style={{
                  fontSize: 10, fontWeight: SEVERITY_COLORS[log.severity]?.fontWeight || 600,
                  padding: '2px 8px', borderRadius: 10,
                  background: SEVERITY_COLORS[log.severity]?.bg || T.textPrimary,
                  color: SEVERITY_COLORS[log.severity]?.color || T.textMuted,
                  textTransform: 'uppercase',
                }}>
                  {log.severity}
                </span>
              )}
              {/* Summary */}
              <span style={{ flex: 1, fontSize: 13, color: T.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {log.summary}
              </span>
              {/* Customer ID */}
              {log.customer_id && (
                <span style={{ fontSize: 11, color: T.textMuted, fontFamily: 'monospace' }}>
                  {log.customer_id.slice(0, 8)}
                </span>
              )}
              {/* IP */}
              {log.ip && (
                <span style={{ fontSize: 11, color: T.textDisabled, fontFamily: 'monospace' }}>
                  {log.ip}
                </span>
              )}
              {/* Expand arrow */}
              <span style={{ color: T.textDisabled, fontSize: 12, transform: expandedId === `${log.type}-${log.id}` ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                              </span>
            </div>
            {/* Expanded details */}
            {expandedId === `${log.type}-${log.id}` && (
              <div style={{
                padding: '12px 18px 12px 100px', background: T.bg,
                borderBottom: `1px solid ${T.border}`,
              }}>
                <pre style={{
                  margin: 0, fontSize: 12, color: T.textSecondary, whiteSpace: 'pre-wrap',
                  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                  lineHeight: 1.6, maxHeight: 300, overflow: 'auto',
                }}>
                  {typeof log.details === 'object' ? JSON.stringify(log.details, null, 2) : String(log.details || '—')}
                </pre>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Load more */}
      {logs.length < total && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Button variant="ghost" onClick={loadMore} disabled={loading} loading={loading}>
            {loading ? <><Spinner size="sm" inline /> Carregando...</> : `Carregar mais (${logs.length}/${total})`}
          </Button>
        </div>
      )}
    </div>
  );
}
